/**
 * Order extras (GET orders/{oid}/extras): tags, manual payment + mark paid, balance due + payment link, gift cards used,
 * digital downloads (reset / extend / revoke / resend), and "Edit order" (add / remove lines before fulfillment).
 * Rendered in the order detail's side column: <ShopOrderExtras order={o} onChanged={reload} />.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Checkbox, Divider, Input, InputNumber, List, Modal, Popconfirm, Space, Table, Tag, Typography, message } from 'antd';
import { EditOutlined, LinkOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import TagsInput from '../../../shared/components/TagsInput';
import { QUIET } from './api';
import { CopyLine, fmtDate, listOf, money, toastErr, useSite } from './common';
import ShopVariantPicker from './ShopVariantPicker';

const { Text } = Typography;

const EditOrderModal = ({ order, open, onClose, onDone }) => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [remove, setRemove] = useState({});
    const [add, setAdd] = useState([]);
    const [opts, setOpts] = useState({ restock: true, notify: true, refund: true, send_invoice: true });
    const [picker, setPicker] = useState(false);
    const [busy, setBusy] = useState(false);
    useEffect(() => { if (open) { setRemove({}); setAdd([]); } }, [open]);
    const items = listOf(order?.items);
    const submit = async () => {
        setBusy(true);
        try {
            const r = await api.post(`/orders/${order.id}/edit`, {
                remove: Object.entries(remove).filter(([, q]) => q > 0).map(([id, quantity]) => ({ order_item_id: Number(id), quantity })),
                add: add.map((a) => ({ variant_id: a.variant_id, quantity: a.quantity })), ...opts,
            });
            message.success(t('Order updated'));
            onDone(r);
        } catch (e) { toastErr(e); } finally { setBusy(false); }
    };
    const changed = Object.values(remove).some((q) => q > 0) || add.length > 0;
    return (
        <Modal open={open} title={t('Edit order')} onCancel={onClose} onOk={submit} okText={t('Update order')} confirmLoading={busy} okButtonProps={{ disabled: !changed }} width={640} destroyOnHidden>
            <Table rowKey="id" size="small" pagination={false} dataSource={items}
                columns={[
                    { title: t('Item', { context: 'shop' }), key: 'i', render: (_, i) => <span>{i.title}{i.variant_title ? <Text type="secondary"> · {i.variant_title}</Text> : null}</span> },
                    { title: t('Qty', { context: 'shop' }), dataIndex: 'quantity', width: 60 },
                    { title: t('Remove'), key: 'r', width: 110, render: (_, i) => <InputNumber size="small" min={0} max={i.quantity - (i.fulfilled_quantity || 0)} value={remove[i.id] || 0} onChange={(q) => setRemove((s) => ({ ...s, [i.id]: q || 0 }))} /> },
                ]} />
            {add.length > 0 && (
                <List size="small" style={{ marginTop: 8 }} header={<Text strong>{t('Add')}</Text>} dataSource={add} renderItem={(a, k) => (
                    <List.Item actions={[<Button key="x" size="small" type="text" danger onClick={() => setAdd(add.filter((_, j) => j !== k))}>×</Button>]}>
                        {a.title}{a.variant_title ? ` · ${a.variant_title}` : ''} × {a.quantity}
                    </List.Item>
                )} />
            )}
            <Button size="small" type="dashed" icon={<PlusOutlined />} style={{ marginTop: 8 }} onClick={() => setPicker(true)}>{t('Add products')}</Button>
            <Divider style={{ margin: '12px 0' }} />
            <Space wrap>
                <Checkbox checked={opts.restock} onChange={(e) => setOpts({ ...opts, restock: e.target.checked })}>{t('Restock removed items')}</Checkbox>
                <Checkbox checked={opts.refund} onChange={(e) => setOpts({ ...opts, refund: e.target.checked })}>{t('Refund the difference')}</Checkbox>
                <Checkbox checked={opts.send_invoice} onChange={(e) => setOpts({ ...opts, send_invoice: e.target.checked })}>{t('Send an invoice for any balance')}</Checkbox>
                <Checkbox checked={opts.notify} onChange={(e) => setOpts({ ...opts, notify: e.target.checked })}>{t('Email the customer')}</Checkbox>
            </Space>
            <ShopVariantPicker open={picker} onClose={() => setPicker(false)} onPick={(it) => setAdd((s) => [...s, it])} />
        </Modal>
    );
};

const ShopOrderExtras = ({ order, onChanged }) => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [x, setX] = useState(null);
    const [tags, setTags] = useState([]);
    const [known, setKnown] = useState([]);
    const [link, setLink] = useState(null);
    const [editing, setEditing] = useState(false);
    const [ref, setRef] = useState('');
    const oid = order?.id;
    const cur = order?.currency;

    const load = useCallback(() => {
        if (!oid) return;
        api.get(`/orders/${oid}/extras`, null, QUIET).then((d) => { setX(d); setTags(listOf(d?.tags)); }).catch(() => setX(false));
    }, [api, oid]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => { api.get('/orders/tags', null, QUIET).then((d) => setKnown(listOf(d?.orders))).catch(() => {}); }, [api]);
    if (!x) return null;

    const saveTags = async (next) => {
        setTags(next);
        try { await api.put(`/orders/${oid}/tags`, { tags: next }); } catch (e) { toastErr(e); }
    };
    const payLink = async () => { try { const r = await api.post(`/orders/${oid}/payment-link`); setLink(r?.url); } catch (e) { toastErr(e); } };
    const markPaid = async () => { try { await api.post(`/orders/${oid}/mark-paid`, { reference: ref }); message.success(t('Marked as paid')); load(); onChanged?.(); } catch (e) { toastErr(e); } };
    const grant = async (g, body) => { try { await api.put(`/orders/${oid}/downloads/${g.id}`, body); load(); } catch (e) { toastErr(e); } };
    const resend = async () => { try { await api.post(`/orders/${oid}/downloads/resend`); message.success(t('Sent', { context: 'shop' })); load(); } catch (e) { toastErr(e); } };

    const pending = order.financial_status === 'pending' && order.number && order.status !== 'cancelled';
    const editable = order.status !== 'cancelled' && order.fulfillment_status === 'unfulfilled' && order.number;
    const downloads = listOf(x.downloads);
    const gcs = listOf(x.gift_cards);
    return (
        <>
            <Card title={t('Tags')} size="small" style={{ marginBottom: 16 }}>
                <TagsInput format="array" value={tags} onChange={saveTags} open={undefined} suffixIcon={undefined} options={known.filter((k) => !tags.includes(k)).map((k) => ({ value: k, label: k }))} />
                {x.source && x.source !== 'web' && <div style={{ marginTop: 6 }}><Text type="secondary" style={{ fontSize: 12 }}>{t('Source')}: {t(x.source === 'draft' ? 'Draft order' : x.source)}</Text></div>}
            </Card>
            {(pending || x.balance_due > 0 || x.manual_payment || editable) && (
                <Card title={t('Payment & changes')} size="small" style={{ marginBottom: 16 }}>
                    {x.manual_payment && <div style={{ marginBottom: 8 }}><Tag>{x.manual_payment.name}</Tag></div>}
                    {pending && (
                        <Space.Compact style={{ width: '100%', marginBottom: 8 }}>
                            <Input size="small" placeholder={t('Reference (optional)')} value={ref} onChange={(e) => setRef(e.target.value)} />
                            <Popconfirm title={t('Mark this order as paid?')} onConfirm={markPaid}><Button size="small" type="primary">{t('Mark as paid')}</Button></Popconfirm>
                        </Space.Compact>
                    )}
                    {x.balance_due > 0 && (
                        <Alert type="warning" showIcon style={{ marginBottom: 8 }} message={t('Balance due: {{amount}}', { amount: money(x.balance_due, cur) })}
                            description={link ? <CopyLine value={link} /> : <Button size="small" icon={<LinkOutlined />} onClick={payLink}>{t('Payment link')}</Button>} />
                    )}
                    {editable && <Button size="small" block icon={<EditOutlined />} onClick={() => setEditing(true)}>{t('Edit order')}</Button>}
                    {listOf(x.edits).length > 0 && (
                        <div style={{ marginTop: 8 }}>
                            {listOf(x.edits).slice(-3).map((e, i) => <div key={i}><Text type="secondary" style={{ fontSize: 12 }}>{fmtDate(e.at || e.created_at)} · {t('Edited')}</Text></div>)}
                        </div>
                    )}
                </Card>
            )}
            {gcs.length > 0 && (
                <Card title={t('Gift cards')} size="small" style={{ marginBottom: 16 }}>
                    {gcs.map((g, i) => <div key={i}><Text code>•••• {g.last4}</Text> <Text>-{money(g.amount, cur)}</Text></div>)}
                </Card>
            )}
            {downloads.length > 0 && (
                <Card title={t('Digital downloads')} size="small" style={{ marginBottom: 16 }} extra={<Button size="small" type="link" onClick={resend}>{t('Resend')}</Button>}>
                    {downloads.map((g) => (
                        <div key={g.id} style={{ marginBottom: 8 }}>
                            <Text>{g.name || g.title}</Text>
                            <div>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    {g.max_downloads ? `${g.downloads}/${g.max_downloads}` : g.downloads} {t('downloads')}{g.expires_at ? ` · ${t('until')} ${fmtDate(g.expires_at, false)}` : ''}
                                </Text>
                                {!g.available && <Tag color="red" style={{ marginLeft: 4 }}>{t('Unavailable')}</Tag>}
                            </div>
                            <Space size={4}>
                                <Button size="small" type="link" style={{ padding: 0 }} onClick={() => grant(g, { reset: true })}>{t('Reset count')}</Button>
                                <Button size="small" type="link" onClick={() => grant(g, { extend_days: 30 })}>{t('+30 days')}</Button>
                                <Button size="small" type="link" danger onClick={() => grant(g, { revoke: g.available })}>{g.available ? t('Revoke', { context: 'shop' }) : t('Restore')}</Button>
                            </Space>
                        </div>
                    ))}
                </Card>
            )}
            <EditOrderModal order={order} open={editing} onClose={() => setEditing(false)} onDone={() => { setEditing(false); load(); onChanged?.(); }} />
        </>
    );
};

export default ShopOrderExtras;
